/**
 * This Service file contains CloudSyncSession Service, which is used by CloudSyncService to execute session related upsert and fetch functions.
 *   1. As a session is edited, or a session's planIds are altered, a session is upserted to purchaseAPI every 60 seconds
 *   2. If the browser tab is suddenly closed, the most recently registered session will be upserted
 * */
import { useRequestQueue } from '@/mixins/requestQueue'
import { windowRequestQueueConfig } from '@/mixins/requestQueueWindowConfig'
import { useCloudSyncDocumentService } from '@/services/CloudSync/cloudSyncDocumentService'
import type { CloudSyncFetchedPlan } from '@/services/CloudSync/cloudSyncPlanService'
import type { CloudSyncInterface } from '@/services/CloudSync/cloudSyncService'
import { Feature, useFeatureService } from '@/services/featureService'
import { purchaseApiAxiosInstance } from '@/services/purchaseApiAxiosInstance'
import { useAuthStore } from '@/stores/auth'
import type { StoredPlanData } from '@/stores/plan'
import type { SessionAnalyticsData, SessionData, UploadItem } from '@/types'
import { captureException } from '@sentry/vue'
import { useDebounceFn } from '@vueuse/core'
import localforageLib from 'localforage'
import localForageLib from 'localforage'
import { computed, ref } from 'vue'

type SessionUpsertRequest = {
	lastUpdated: string
	crmAppointmentId?: string | null
	salesforceEventAppointmentId?: string | null
	salesforceContactAfpId: string
	planIds: string[]
	salesforceAccountFuneralHomeId: string
	salesforceFuneralHomeCrossSetGroupId?: string
	startTime?: string | null
	endTime?: string | null
	analyticsData?: SessionAnalyticsData
	resultedAt?: string | null
	sessionData?: SessionData
}

type CloudSyncSessionResponse = {
	data: CloudSyncFetchedSession[]
}

export type CloudSyncFetchedSession = {
	id: string
	crmAppointmentId: string
	salesforceEventAppointmentId: string
	lastUpdated: string
	hubUserId: string
	salesforceContactAfpId: string
	salesforceAccountFuneralHomeId: string
	salesforceFuneralHomeCrossSetGroupId: string
	startTime: string
	endTime: string
	analyticsData: SessionAnalyticsData
	resultedAt: string
	sessionData: SessionData
	plans: CloudSyncFetchedPlan[]
}

type SessionIdAndLastUpdated = Pick<SessionData, 'id' | 'lastUpdated'>

export interface CloudSyncSessionInterface
	extends CloudSyncInterface<SessionData> {
	fetchWalkInSessionsFromServer: () => Promise<void>
}

export const useCloudSyncSessionService = (
	logFn: ((message: string) => void) | undefined = undefined
): CloudSyncSessionInterface => {
	const localKeys = localForageLib.createInstance({
		name: 'encryption',
		storeName: 'localKeys',
	})

	const serviceName = 'session'
	const sessionsToUpsert = ref<Set<string>>(new Set<string>())
	const debouncePeriod = Number(
		import.meta.env.VITE_CLOUD_SYNC_SESSION_DEBOUNCE || 15000
	)

	const localSessions = localforageLib.createInstance({
		name: 'sessions',
		storeName: 'sessions',
	})

	const localPlans = localforageLib.createInstance({
		name: 'sessions',
		storeName: 'plans',
	})

	const authStore = useAuthStore()
	const featureService = useFeatureService()
	const cloudSyncDocumentService = useCloudSyncDocumentService()

	const debugLog = (message: string): void => {
		if (logFn) {
			logFn('[Session] ' + message)
		}
	}

	const upsertQueueConfig = {
		axiosInstance: purchaseApiAxiosInstance,
		requestBackgroundSync: async (tag: string) => {
			await windowRequestQueueConfig.requestBackgroundSync('cloud-sync-upsert')
		},
		clearCompleted: true,
	}

	const upsertQueue = useRequestQueue(
		'cloudSyncSessionUpsert',
		upsertQueueConfig
	)

	// Fetch sessions for an AFP, used in sync when all sync processes have completed
	const fetchWalkInSessionsFromServer = async () => {
		if (!featureService.isEnabled(Feature.CloudSync)) {
			return
		}

		const salesforceContactAfpId = authStore.getActiveSalesContactId()

		if (!salesforceContactAfpId) {
			// can't fetch without an AFP id
			return
		}

		const sessionsWithLastUpdated: Array<SessionIdAndLastUpdated> =
			await getLocalSessionsWithLastUpdated()

		const payload = {
			salesforceContactAfpId,
			sessions: sessionsWithLastUpdated,
		}

		// call the API
		try {
			const response =
				await purchaseApiAxiosInstance.post<CloudSyncSessionResponse>(
					'v1/cloud-sync/fetch-sessions',
					payload
				)
			const cloudSyncWalkInSessionData = response?.data?.data

			await Promise.allSettled(
				cloudSyncWalkInSessionData.map(async (cloudSyncWalkInSession) => {
					try {
						if (!cloudSyncWalkInSession.sessionData) {
							return
						}
						const localCopy = sessionsWithLastUpdated.find(
							(localSession) =>
								localSession.id.toLowerCase() ===
								cloudSyncWalkInSession.id.toLowerCase()
						)

						// if local session store does not have the remote session we insert it
						// else we only insert if the CloudSync version is newer
						const cloudSyncIsNewer =
							!localCopy ||
							Date.parse(localCopy?.lastUpdated!) <
								Date.parse(cloudSyncWalkInSession.lastUpdated!)

						if (cloudSyncIsNewer) {
							await insertCloudSyncWalkInSessionToLocalSession(
								cloudSyncWalkInSession.sessionData
							)

							// fetch docs concurrently for walk in plans
							await Promise.allSettled(
								cloudSyncWalkInSession.plans.map((plan) =>
									cloudSyncDocumentService.fetchFromServer(
										plan.id.toLowerCase()
									)
								)
							)

							// Overwrite the local plans with the cloud-sync plan data
							for (const cloudSyncWalkInPlan of cloudSyncWalkInSession.plans) {
								const localPlanId = cloudSyncWalkInPlan.id.toLowerCase()
								await localPlans.setItem(
									localPlanId,
									normalizeUuid(cloudSyncWalkInPlan.planData)
								)
								await localKeys.setItem(
									localPlanId,
									cloudSyncWalkInPlan.encryptionMetadata
								)
							}
						}
					} catch (error) {
						console.error(
							`Failed to sync CloudSync walk-in session ${cloudSyncWalkInSession.id}`,
							error
						)
					}
				})
			)
		} catch (e) {
			console.error(e)
		}
	}

	const normalizeUuid = (planData: StoredPlanData) => {
		planData.basic.id = planData.basic.id.toLowerCase()

		return planData
	}

	const insertCloudSyncWalkInSessionToLocalSession = async (
		session: SessionData
	): Promise<void> => {
		await localSessions.setItem(session.id.toLowerCase(), session)
	}

	/**
	 * Turn all the sessions in local storage into a tuple of id and lastUpdated
	 */
	const getLocalSessionsWithLastUpdated = async () => {
		const sessionInfo: Array<SessionIdAndLastUpdated> = []

		try {
			await localSessions.iterate(function (sessionValue: SessionData, id) {
				const session = { id, lastUpdated: sessionValue.lastUpdated }
				// we only filter plans that already have a timestamp, for plans that do not have a lastUpdated, we will give it one when it's edited (watcher in SessionView)
				if (sessionValue.lastUpdated) {
					sessionInfo.push(session)
				}
			})
		} catch (err) {
			console.error(err)
		}

		return sessionInfo
	}

	/**
	 * Will register a sessionId into sessionsToUpsert list, the session in the list will be upserted to purchaseAPI after a debounce period
	 */
	const registerForUpsert = async (sessionId: string) => {
		if (!featureService.isEnabled(Feature.CloudSync)) {
			return
		}

		debugLog(`SessionId: ${sessionId} registered for upsert.`)

		sessionsToUpsert.value.add(sessionId)

		// call throttled upsert
		upsertSessions().catch()
	}

	const checkSessionsToUpsert = () => {
		sessionsToUpsert.value.forEach((sessionId) => {
			upsertSession(sessionId).catch((e) => {
				console.error(
					'something failed while upserting a session to CloudSync\n Error: ' +
						e.message
				)
			})
		})
	}

	const upsertSessions = useDebounceFn(checkSessionsToUpsert, debouncePeriod, {
		maxWait: debouncePeriod,
	})

	const flushPendingUpserts = async () => {
		await Promise.all(
			Array.from(sessionsToUpsert.value).map(async (sessionId: string) => {
				return upsertSession(sessionId)
			})
		)
			.then(() => debugLog('Successfully flushed session sync queue'))
			.catch(() => debugLog('Failed to flush session sync queue'))
	}

	/**
	 * Retrieve an encrypted Session from local forage given the id,
	 * validate and map the data for upsert request, queue up the request
	 */
	const upsertSession = async (sessionId: string) => {
		return navigator.locks.request(
			`cloud-sync-session-${sessionId}`,
			async () => {
				debugLog(`Starting upsert for sessionId ${sessionId}`)

				let session = await localSessions.getItem<SessionData>(sessionId)
				// we only want the session not the plan data
				if (!session || !validateForUpsert(session)) {
					sessionsToUpsert.value.delete(sessionId)
					debugLog(
						`Session was invalid for upsert, deleting ${sessionId} from queue`
					)
					return
				}

				const requestData = await mapUpsertSessionData(session)

				const upsertRequestConfig = {
					url: `/v1/cloud-sync/session/${sessionId}`,
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					data: requestData,
				}

				try {
					await upsertQueue.addRequest(sessionId, upsertRequestConfig)
				} catch (error: any) {
					console.error(
						`Error occurred while trying to upsert session ${sessionId}`,
						error
					)

					const hasUnexpectedResponse =
						error.response && error.response.status !== 401
					const hasUnrecoverableError = !error.response

					if (hasUnexpectedResponse || hasUnrecoverableError) {
						captureException(error, {
							extra: {
								message:
									'Unexpected error happened during upserting Session to CloudSync',
							},
						})
					}
				} finally {
					sessionsToUpsert.value.delete(sessionId)
					debugLog(`Removing sessionId ${sessionId} from queue`)
				}
			}
		)
	}

	const validateForUpsert = (session: SessionData): boolean => {
		const hasSalesforceContactAfpId = !!authStore.getActiveSalesContactId()
		const hasLastUpdated = !!session.lastUpdated

		const hasFuneralHomeId = !!session.FuneralHomeLocationId
		const hasPlans = session.planIds.length > 0

		return (
			hasLastUpdated &&
			hasSalesforceContactAfpId &&
			hasPlans &&
			hasFuneralHomeId
		)
	}

	const mapUpsertSessionData = async (
		session: SessionData
	): Promise<SessionUpsertRequest> => {
		// TODO: update fields when salesForce data is updated

		return {
			lastUpdated: session.lastUpdated!,
			crmAppointmentId: session.appointmentId!,
			salesforceEventAppointmentId: session.salesforceEventAppointmentId,
			salesforceContactAfpId: authStore.getActiveSalesContactId()!,
			planIds: session.planIds!,
			salesforceAccountFuneralHomeId: session.FuneralHomeLocationId!,
			analyticsData: session.analytics,
			startTime: session.startAt,
			resultedAt: session.resultedAt,
			sessionData: session,
		}
	}

	const getPlanIdsFromUploadItem = (
		upsertRequest: UploadItem<SessionUpsertRequest>
	): string[] => {
		return upsertRequest.requestConfig.data?.planIds!
	}

	/**
	 * Retry all failed/queued upsert requests, called whenever the app is online
	 */
	const retrySync = async () => {
		try {
			await upsertQueue.retryAll()
		} catch (e) {
			console.error(`Some sessions was not able to be upserted at this try.`)
		}
	}

	//Placeholder for interface
	const clearStorage = async () => {
		return
	}

	const purge = async () => {
		await clearStorage()
		await upsertQueue.clearPendingRequests()
	}

	// When the app is closed or becomes invisible, all the pending plan updates is sent to purchase API
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState == 'hidden') {
			checkSessionsToUpsert()
		}
	})

	const pendingDebounces = computed((): number => sessionsToUpsert.value.size)

	const getRelatedPlanIdsForPendingUpserts = async () => {
		const uniquePlanIds = new Set(
			await upsertQueue.getRelatedPlanIdsForPendingUpserts<SessionUpsertRequest>(
				getPlanIdsFromUploadItem
			)
		)

		return Array.from(uniquePlanIds)
	}

	return {
		debouncePeriod,
		upsertQueue,
		registerForUpsert,
		validateForUpsert,
		retrySync,
		fetchWalkInSessionsFromServer,
		clearStorage,
		purge,
		flushPendingUpserts,
		pendingDebounces,
		serviceName,
		getRelatedPlanIdsForPendingUpserts,
	}
}
